// Keep unsaved editor work in localStorage.
//
// Admin sessions expire, and the API client handles a 401 with a hard
// navigation to the login page — which destroys every bit of in-memory
// React state, including a half-written product someone had open in a
// background tab. Saving to the server wouldn't help: the request that
// would carry it is the one being rejected.
//
// localStorage survives the bounce, the login, the tab closing and the
// browser restarting, so the draft is still there afterwards.
import { useCallback, useEffect, useRef, useState } from 'react';

const PREFIX = 'bleums:draft:';
// Long enough to survive a night, short enough that a draft abandoned last
// month doesn't ambush you when you open a new form.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Typing shouldn't hit storage on every keystroke.
const DEBOUNCE_MS = 800;

interface Stored<T> {
  savedAt: number;
  value: T;
}

interface Options<T> {
  /** Stable per editor, e.g. `product:new` or `order:{id}`. */
  key: string;
  /** The live draft. Persisted whenever it differs from the baseline. */
  value: T;
  /** False while loading, so an empty form isn't saved over a real draft. */
  ready: boolean;
  /** Called once, with a recovered draft, if there is one. */
  onRestore: (value: T) => void;
}

export const useDraftAutosave = <T,>({ key, value, ready, onRestore }: Options<T>) => {
  const storageKey = PREFIX + key;
  const [restoredAt, setRestoredAt] = useState<number | undefined>();
  // What the editor started from. Anything equal to it isn't worth saving,
  // so merely opening a form never leaves a draft behind.
  const baseline = useRef<string | undefined>(undefined);
  const restoreChecked = useRef(false);

  const clearDraft = useCallback(() => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // Private mode, quota, disabled storage — nothing to do about it.
    }
    setRestoredAt(undefined);
  }, [storageKey]);

  // Recover once, as soon as the editor has its starting point.
  useEffect(() => {
    if (!ready || restoreChecked.current) return;
    restoreChecked.current = true;
    baseline.current = JSON.stringify(value);

    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const stored: Stored<T> = JSON.parse(raw);
      if (!stored?.savedAt || Date.now() - stored.savedAt > MAX_AGE_MS) {
        window.localStorage.removeItem(storageKey);
        return;
      }
      // Nothing to recover if it matches what we'd show anyway.
      if (JSON.stringify(stored.value) === baseline.current) {
        window.localStorage.removeItem(storageKey);
        return;
      }
      onRestore(stored.value);
      setRestoredAt(stored.savedAt);
    } catch {
      // A draft we can't parse is worse than none.
      clearDraft();
    }
    // Runs on the transition into ready; re-running would re-restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    if (!ready || !restoreChecked.current) return;
    const serialized = JSON.stringify(value);
    if (serialized === baseline.current) return;

    const timer = window.setTimeout(() => {
      try {
        const record: Stored<T> = { savedAt: Date.now(), value };
        window.localStorage.setItem(storageKey, JSON.stringify(record));
      } catch {
        // Out of quota or storage blocked; the editor still works.
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [value, ready, storageKey]);

  /** Call after a successful save: what's on the server now *is* the draft. */
  const commit = useCallback(
    (savedValue: T) => {
      baseline.current = JSON.stringify(savedValue);
      clearDraft();
    },
    [clearDraft]
  );

  /** Throw the recovered draft away and go back to the server's copy. */
  const discard = useCallback(
    (serverValue: T) => {
      baseline.current = JSON.stringify(serverValue);
      onRestore(serverValue);
      clearDraft();
    },
    [clearDraft, onRestore]
  );

  return { restoredAt, commit, discard };
};

export const draftAgeLabel = (savedAt: number): string => {
  const minutes = Math.round((Date.now() - savedAt) / 60000);
  if (minutes < 1) return 'moments ago';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
};
